import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const handleCallback = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionId = new URLSearchParams(hash.slice(1)).get('session_id');

        console.info('[AuthCallback] origin:', window.location.origin, 'host:', window.location.hostname, 'has_session_id:', !!sessionId);

        if (!sessionId) {
          toast.error('Invalid authentication response');
          navigate('/');
          return;
        }

        // CRITICAL: Clear the hash from browser URL BEFORE the exchange.
        // Session IDs are single-use. If this tab is refreshed or the user
        // taps Back, a hash with an already-consumed session_id would
        // trigger AuthCallback again and 404 ("Invalid session ID").
        try {
          const cleanUrl = window.location.pathname + window.location.search;
          window.history.replaceState(null, '', cleanUrl);
        } catch (e) { /* noop */ }

        // Exchange session_id for user data and cookie
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const response = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        const user = response.data;

        // Navigate to assessment or dashboard with user data
        navigate('/assessment', {
          state: { user },
          replace: true
        });
      } catch (error) {
        console.error('[AuthCallback] session exchange failed:', {
          message: error?.message,
          status: error?.response?.status,
          data: error?.response?.data,
          api: `${API}/auth/google/session`,
          origin: typeof window !== 'undefined' ? window.location.origin : 'n/a',
        });
        const detail = error?.response?.data?.detail || '';
        // Special-case the single-use replay error
        const isReplay = /invalid session id/i.test(detail);
        if (isReplay) {
          toast.error('This login link has already been used. Please sign in again.');
        } else {
          toast.error(detail ? `Authentication failed: ${detail}` : 'Authentication failed');
        }
        navigate('/');
      }
    };

    handleCallback();
  }, [location, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <div className="spinner w-12 h-12 border-4 border-[#6366F1] border-t-transparent rounded-full mb-4 mx-auto" />
        <p className="text-gray-400">Completing authentication...</p>
      </div>
    </div>
  );
}

export default AuthCallback;
